import {ConfigUtil} from '@mintdata-wsp/common/util/ConfigUtil';
import {WindowLocationUtil, WSP_HOST} from '@mintdata-wsp/common/util/WindowLocationUtil';
import * as React from 'react';
import * as classnames from 'classnames/bind';
import bind from 'bind-decorator';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {observer} from 'mobx-react';
import {Dialog} from '@mintdata-wsp/wsp/ui/components/forms/Dialog';
import {Column} from '@mintdata-wsp/wsp/ui/components/forms/Column';
import {links} from '@mintdata-wsp/wsp/ui/routes/Routing';
import {TextRun} from '@mintdata-wsp/wsp/ui/components/text/TextRun';
import {MDIcons} from '@mintdata-wsp/common/components/icon/MDIcons';
import {Row} from '@mintdata-wsp/wsp/ui/components/forms/Row';
import {Form, IFormData} from '@mintdata-wsp/common/components/form/Form';
import {Input} from '@mintdata-wsp/common/components/input/Input';
import {Button} from '@mintdata-wsp/common/components/button/Button';
import {ErrorPopupSide} from '@mintdata-wsp/common/components/formField/FormField';
import {action} from 'mobx';
import {DialogError} from '@mintdata-wsp/wsp/ui/components/forms/DialogError';
import {Spinner, SpinnerSize, SpinnerStyle} from '@mintdata-wsp/common/components/spinner/Spinner';
import {rootState} from '@mintdata-wsp/common/state/RootState';
import _get from 'lodash/get';

const styles = require('./LoginForm.less');
const cx = classnames.bind(styles);

interface IMessageFromGoogleSignInIframe
{
    email:string;
    fullname:string;
    id_token:string;
}

const iframeDataPrefix:string = 'WSP_G_SIGN_IN_DATA_';
// will be replaced to mintdata.com after release of website with google sign in page
const iframeOrigin:string = `https://${WindowLocationUtil.isLocal() ? WSP_HOST.DEV : WindowLocationUtil.getRootDomain()}`;

@observer
export class LoginForm extends React.Component<any, any>
{
    private _form:Form = new Form({
        onSubmit: this._onFormSubmit
    });

    componentDidMount()
    {
        window.addEventListener('message', this._receiveMessage, false);
    }

    componentWillUnmount()
    {
        window.removeEventListener('message', this._receiveMessage, false);
    }

    @bind
    private _receiveMessage(event:MessageEvent)
    {
        if (event.origin !== iframeOrigin)
            return;

        if (typeof event.data === 'string' && event.data.startsWith(iframeDataPrefix))
        {
            const {email, fullname, id_token}:IMessageFromGoogleSignInIframe =
                JSON.parse(event.data.substr(iframeDataPrefix.length));

            controllers.ui.login.signInViaGoogle(email, fullname, id_token, this._form);
        }
    }

    @bind
    private _onFormSubmit(formData:IFormData)
    {
        const nextPathname = _get(this.props, 'location.state.from.pathname');

        controllers.ui.login.submitForm(this._form, nextPathname);
    }

    @action.bound
    _onLogin():void
    {
        this._form.submit();
    }

    private _getSpinner():JSX.Element
    {
        return <Spinner size={SpinnerSize.Medium_24} style={SpinnerStyle.White} />;
    }

    render()
    {
        const controller = controllers.ui.login;
        const {isLoginInProgress} = rootState.ui.loginPage;

        const commonErrorMessage = this._form.getServerError('_');
        const showGoogleSignInButton:boolean = ConfigUtil.isGoogleSignInButtonEnabled();

        return (
            <Dialog className={cx('loginDialog')} title={'Login'}>

                <DialogError message={commonErrorMessage} />

                <div className={cx('loginForm')}>
                    <div className={cx('iframe')}>
                        {showGoogleSignInButton && <iframe
                            src={`${iframeOrigin}/googleAuth`}
                            frameBorder={'0'}
                            style={{width: '100%', height: '100%'}}
                        />}
                    </div>

                    <Column>

                        <Input icon={MDIcons.common.Email}
                               className={cx('input')}
                               placeholder={'john@appleseed.com'}
                               form={this._form}
                               formKey={'email'}
                               isInitiallyFocused={true}
                               validationFn={controller.validateEmail}
                               errorPopupSide={ErrorPopupSide.Right}
                        />

                        <Input icon={MDIcons.common.PasswordKey}
                               className={cx('input')}
                               placeholder={'Password'}
                               isPassword={true}
                               form={this._form}
                               formKey={'password'}
                               validationFn={controller.validatePassword}
                               errorPopupSide={ErrorPopupSide.Right}
                        />

                        <TextRun className={cx('forgot-password-row')}>
                            {links.toForgotPassword('Forgot password?', cx('forgot-password-link'))}
                        </TextRun>
                    </Column>

                    <Row className={cx('mainButtonRow')}>
                        <Button caption={'LOGIN'}
                                className={cx('mainButton')}
                                onClick={this._onLogin}
                                icon={isLoginInProgress ? this._getSpinner() : null}
                                relativeIcon={true}
                                isDisabled={isLoginInProgress}
                        />
                    </Row>

                    <TextRun>
                        Don't have an account?
                        {links.toSignUp('Sign Up', cx('actionLink'))}
                    </TextRun>

                </div>

            </Dialog>
        );
    }
}
